import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  LuArrowLeft,
  LuCreditCard,
  LuRefreshCw,
  LuReceipt,
  LuGift,
  LuMail,
  LuUndo2,
} from "react-icons/lu";
import {
  api,
  ApiError,
  type ConsoleChargeRow,
  type ConsoleTier,
  type ConsoleUserDetail,
} from "@/lib/api";
import { Card, CardHeader, CardTitle, CardBody } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PageHeader } from "@/components/ui/page-header";

const TIERS: ConsoleTier[] = ["free", "pro", "business"];

const TIER_RANK: Record<string, number> = { free: 0, pro: 1, business: 2, enterprise: 3 };

type StripeAction = "cancel_now" | "cancel_at_end" | "keep";

export function UserDetailPage() {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();

  const userQuery = useQuery({
    queryKey: ["console", "users", id],
    queryFn: () => api.users.get(id),
    enabled: Boolean(id),
  });

  const user = userQuery.data;

  const [tier, setTier] = useState<ConsoleTier | null>(null);
  const [stripeAction, setStripeAction] = useState<StripeAction>("cancel_now");
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const currentTier = user?.subscriptionTier ?? "free";
  const selectedTier = tier ?? (currentTier === "enterprise" ? "business" : currentTier);
  const isDowngrade = TIER_RANK[selectedTier] < TIER_RANK[currentTier];
  const hasLiveSubscription = useMemo(
    () =>
      (user?.subscriptions ?? []).some((s) =>
        ["active", "trialing", "past_due", "unpaid"].includes(s.status),
      ),
    [user],
  );

  const planMutation = useMutation({
    mutationFn: () =>
      api.users.setPlan(id, {
        tier: selectedTier,
        stripeAction: isDowngrade ? stripeAction : "keep",
        reason: reason || undefined,
      }),
    onSuccess: (res) => {
      toast.success(
        res.appliedImmediately
          ? `Plan set to ${res.tier}${
              res.cancelledSubscriptions.length
                ? ` (cancelled ${res.cancelledSubscriptions.length} Stripe subscription${
                    res.cancelledSubscriptions.length === 1 ? "" : "s"
                  })`
                : ""
            }`
          : "Scheduled: access continues until the billing period ends",
      );
      setConfirmOpen(false);
      setReason("");
      setTier(null);
      queryClient.invalidateQueries({ queryKey: ["console", "users"] });
    },
    onError: (err: Error) =>
      toast.error(err instanceof ApiError ? err.message : err.message),
  });

  const syncMutation = useMutation({
    mutationFn: () => api.users.syncBilling(id),
    onSuccess: (res) => {
      toast.success(
        res.synced
          ? res.change
            ? "Synced - account corrected from Stripe"
            : "Synced - already up to date"
          : `Nothing to sync (${res.reason ?? "no Stripe customer"})`,
      );
      queryClient.invalidateQueries({ queryKey: ["console", "users"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (userQuery.isLoading) {
    return (
      <>
        <PageHeader title="User" icon={LuCreditCard} description="Loading..." />
        <div className="p-8 text-sm text-[var(--color-text-muted)]">Loading account...</div>
      </>
    );
  }

  if (userQuery.isError || !user) {
    return (
      <>
        <PageHeader title="User" icon={LuCreditCard} description="Not found" />
        <div className="p-8 text-sm text-[var(--color-error)]">
          Couldn't load this account.{" "}
          <Link to="/users" className="underline">
            Back to users
          </Link>
        </div>
      </>
    );
  }

  const planChanged = selectedTier !== currentTier;

  return (
    <>
      <PageHeader
        title={user.email}
        icon={LuCreditCard}
        description={`${user.subscriptionTier} - ${user.subscriptionStatus}${
          user.subscriptionSource ? ` - managed by ${user.subscriptionSource}` : ""
        }`}
      />

      <div className="p-8 space-y-6">
        <Link
          to="/users"
          className="inline-flex items-center gap-2 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          <LuArrowLeft className="w-3.5 h-3.5" /> All users
        </Link>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader className="flex items-center justify-between">
              <CardTitle>Plan</CardTitle>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending || !user.stripeCustomerId}
                title={
                  user.stripeCustomerId
                    ? "Re-read this customer from Stripe and correct the tier"
                    : "No Stripe customer on this account"
                }
              >
                <LuRefreshCw
                  className={`w-3.5 h-3.5 ${syncMutation.isPending ? "animate-spin" : ""}`}
                />
                Sync from Stripe
              </Button>
            </CardHeader>
            <CardBody className="space-y-4">
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <Field label="Current tier" value={user.subscriptionTier} />
                <Field label="Status" value={user.subscriptionStatus} />
                <Field label="Source" value={user.subscriptionSource ?? "stripe"} />
                <Field
                  label="Past due since"
                  value={user.pastDueSince ? new Date(user.pastDueSince).toLocaleDateString() : "-"}
                />
                <Field
                  label="Stripe customer"
                  value={user.stripeCustomerId ?? "none"}
                  mono
                />
                <Field label="API credits" value={String(user.apiCredits ?? 0)} />
              </dl>

              <div className="border-t border-[var(--color-border)] pt-4 space-y-3">
                <div className="flex flex-wrap gap-2">
                  {TIERS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTier(t)}
                      className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-md border transition-colors ${
                        selectedTier === t
                          ? "border-[var(--color-accent)] text-[var(--color-text)] bg-[rgba(139,92,246,0.12)]"
                          : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-border-hover)]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>

                {isDowngrade && hasLiveSubscription && (
                  <label className="block text-xs text-[var(--color-text-muted)] space-y-1">
                    Live Stripe subscription
                    <select
                      value={stripeAction}
                      onChange={(e) => setStripeAction(e.target.value as StripeAction)}
                      className="w-full mt-1 px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent)]"
                    >
                      <option value="cancel_now">Cancel now - access ends immediately</option>
                      <option value="cancel_at_end">
                        Cancel at period end - keeps access until paid period ends
                      </option>
                      <option value="keep">
                        Leave Stripe alone - billing continues (comp / manual)
                      </option>
                    </select>
                  </label>
                )}

                <Input
                  placeholder="Reason (recorded in the audit log)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />

                <Button
                  variant={isDowngrade ? "danger" : "primary"}
                  disabled={!planChanged || planMutation.isPending}
                  onClick={() => setConfirmOpen(true)}
                >
                  {!planChanged
                    ? "No change selected"
                    : isDowngrade
                      ? `Downgrade to ${selectedTier}`
                      : `Upgrade to ${selectedTier}`}
                </Button>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Stripe subscriptions</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              {user.subscriptions.length === 0 ? (
                <p className="text-sm text-[var(--color-text-muted)]">
                  No subscription on file. If this account has a paid tier, it was granted
                  manually or predates the billing mirror - use "Sync from Stripe".
                </p>
              ) : (
                user.subscriptions.map((sub) => (
                  <div
                    key={sub.id}
                    className="rounded-md border border-[var(--color-border)] p-3 text-sm space-y-1"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono text-xs text-[var(--color-text-muted)]">
                        {sub.id}
                      </span>
                      <span className="text-xs uppercase tracking-wider">{sub.status}</span>
                    </div>
                    <div className="text-[var(--color-text-muted)] text-xs">
                      {sub.tier} - {formatMoney(sub.amount_cents, sub.currency)} /{" "}
                      {sub.interval ?? "?"}
                      {sub.current_period_end
                        ? ` - renews ${new Date(sub.current_period_end).toLocaleDateString()}`
                        : ""}
                      {sub.cancel_at_period_end ? " - cancels at period end" : ""}
                    </div>
                  </div>
                ))
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="flex items-center gap-2">
              <LuReceipt className="w-4 h-4 text-[var(--color-text-muted)]" />
              <CardTitle>Invoices</CardTitle>
            </CardHeader>
            <CardBody className="space-y-2">
              {user.invoices.length === 0 ? (
                <p className="text-sm text-[var(--color-text-muted)]">No invoices recorded.</p>
              ) : (
                user.invoices.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between text-sm gap-3">
                    <span className="text-[var(--color-text-muted)] text-xs tabular-nums">
                      {new Date(inv.created_at).toLocaleDateString()}
                    </span>
                    <span
                      className={
                        inv.failed_at
                          ? "text-[#fdba74] text-xs uppercase"
                          : "text-[#6ee7b7] text-xs uppercase"
                      }
                    >
                      {inv.status ?? (inv.failed_at ? "failed" : "paid")}
                      {inv.attempt_count > 1 ? ` (${inv.attempt_count} attempts)` : ""}
                    </span>
                    <span className="tabular-nums">
                      {formatMoney(inv.amount_paid || inv.amount_due, inv.currency)}
                    </span>
                    {inv.hosted_invoice_url && (
                      <a
                        href={inv.hosted_invoice_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs underline text-[var(--color-text-muted)]"
                      >
                        view
                      </a>
                    )}
                  </div>
                ))
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="flex items-center gap-2">
              <LuGift className="w-4 h-4 text-[var(--color-text-muted)]" />
              <CardTitle>Operator grants</CardTitle>
            </CardHeader>
            <CardBody className="space-y-2">
              {user.comps.length === 0 ? (
                <p className="text-sm text-[var(--color-text-muted)]">
                  No comps, refunds or manual plan changes.
                </p>
              ) : (
                user.comps.map((comp) => (
                  <div key={comp.id} className="text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs uppercase tracking-wider">{comp.kind}</span>
                      <span className="text-xs text-[var(--color-text-muted)] tabular-nums">
                        {new Date(comp.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)]">
                      {comp.reason ?? "-"}
                      {comp.granted_by ? ` - by ${comp.granted_by}` : ""}
                      {comp.expires_at
                        ? ` - expires ${new Date(comp.expires_at).toLocaleDateString()}`
                        : ""}
                    </div>
                  </div>
                ))
              )}
            </CardBody>
          </Card>
        </div>

        <RefundsCard userId={id} hasCustomer={Boolean(user.stripeCustomerId)} />

        <Card>
          <CardHeader className="flex items-center gap-2">
            <LuMail className="w-4 h-4 text-[var(--color-text-muted)]" />
            <CardTitle>Recent inboxes</CardTitle>
          </CardHeader>
          <CardBody>
            {user.recentInboxes.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)]">No inboxes.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {user.recentInboxes.slice(0, 10).map((inbox) => (
                  <li key={inbox.id} className="flex items-center justify-between gap-3">
                    <span className="font-mono text-xs">{inbox.address}</span>
                    <span className="text-xs text-[var(--color-text-muted)] tabular-nums">
                      expires {new Date(inbox.expires_at).toLocaleDateString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        destructive={isDowngrade}
        pending={planMutation.isPending}
        title={isDowngrade ? "Downgrade this account?" : "Change this account's plan?"}
        description={
          <PlanChangeSummary
            user={user}
            to={selectedTier}
            isDowngrade={isDowngrade}
            stripeAction={stripeAction}
            hasLiveSubscription={hasLiveSubscription}
          />
        }
        confirmLabel={isDowngrade ? "Downgrade" : "Apply"}
        onConfirm={() => planMutation.mutate()}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}

/**
 * Charges + refunds. Charges are read live from Stripe (we do not mirror
 * them), so this card loads on demand rather than with the account.
 */
function RefundsCard({ userId, hasCustomer }: { userId: string; hasCustomer: boolean }) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<ConsoleChargeRow | null>(null);

  const chargesQuery = useQuery({
    queryKey: ["console", "users", userId, "charges"],
    queryFn: () => api.users.charges(userId),
    enabled: hasCustomer,
  });

  const refundMutation = useMutation({
    mutationFn: (charge: ConsoleChargeRow) =>
      api.users.refund(userId, { chargeId: charge.id, reason: "requested_by_customer" }),
    onSuccess: (res) => {
      toast.success(`Refunded ${(res.amount / 100).toFixed(2)} (${res.status})`);
      setPending(null);
      queryClient.invalidateQueries({ queryKey: ["console", "users", userId, "charges"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const charges = chargesQuery.data?.charges ?? [];

  return (
    <>
      <Card>
        <CardHeader className="flex items-center gap-2">
          <LuUndo2 className="w-4 h-4 text-[var(--color-text-muted)]" />
          <CardTitle>Payments and refunds</CardTitle>
        </CardHeader>
        <CardBody className="space-y-2">
          {!hasCustomer ? (
            <p className="text-sm text-[var(--color-text-muted)]">
              No Stripe customer on this account.
            </p>
          ) : chargesQuery.isLoading ? (
            <p className="text-sm text-[var(--color-text-muted)]">Loading charges...</p>
          ) : chargesQuery.isError ? (
            <p className="text-sm text-[var(--color-error)]">Couldn't load charges.</p>
          ) : charges.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">No charges on record.</p>
          ) : (
            charges.map((charge) => {
              const refundable =
                charge.status === "succeeded" && charge.amountRefunded < charge.amount;
              return (
                <div
                  key={charge.id}
                  className="flex items-center justify-between gap-3 text-sm border-b border-[var(--color-border)] last:border-0 py-2"
                >
                  <div className="min-w-0">
                    <div className="tabular-nums">
                      {formatMoney(charge.amount, charge.currency)}
                      {charge.amountRefunded > 0 && (
                        <span className="text-[var(--color-text-muted)] text-xs">
                          {" "}
                          ({formatMoney(charge.amountRefunded, charge.currency)} refunded)
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)] truncate">
                      {new Date(charge.created * 1000).toLocaleDateString()} -{" "}
                      {charge.description ?? charge.id}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {charge.receiptUrl && (
                      <a
                        href={charge.receiptUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs underline text-[var(--color-text-muted)]"
                      >
                        receipt
                      </a>
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={!refundable || refundMutation.isPending}
                      onClick={() => setPending(charge)}
                    >
                      {charge.refunded ? "Refunded" : "Refund"}
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </CardBody>
      </Card>

      <ConfirmDialog
        open={Boolean(pending)}
        destructive
        pending={refundMutation.isPending}
        title="Refund this charge?"
        description={
          pending ? (
            <span className="text-sm text-[var(--color-text-muted)]">
              Refunds {formatMoney(pending.amount - pending.amountRefunded, pending.currency)}{" "}
              to the customer's original payment method. This cannot be undone, and it does
              not change their plan.
            </span>
          ) : null
        }
        confirmLabel="Refund"
        onConfirm={() => pending && refundMutation.mutate(pending)}
        onClose={() => setPending(null)}
      />
    </>
  );
}

function PlanChangeSummary({
  user,
  to,
  isDowngrade,
  stripeAction,
  hasLiveSubscription,
}: {
  user: ConsoleUserDetail;
  to: ConsoleTier;
  isDowngrade: boolean;
  stripeAction: StripeAction;
  hasLiveSubscription: boolean;
}) {
  return (
    <div className="space-y-2 text-sm text-[var(--color-text-muted)]">
      <p>
        <span className="text-[var(--color-text)]">{user.email}</span>:{" "}
        {user.subscriptionTier} to {to}.
      </p>
      {isDowngrade && hasLiveSubscription && (
        <p>
          {stripeAction === "cancel_now" &&
            "Their Stripe subscription is cancelled immediately. No refund is issued."}
          {stripeAction === "cancel_at_end" &&
            "Their Stripe subscription is set to cancel at the end of the paid period; the tier drops when Stripe closes it."}
          {stripeAction === "keep" &&
            "Stripe is left untouched - they keep being billed at the old price."}
        </p>
      )}
      <p>The account is marked operator-managed, so Stripe sync will not undo this.</p>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-[var(--color-text-subtle)]">
        {label}
      </dt>
      <dd className={`text-[var(--color-text)] ${mono ? "font-mono text-xs break-all" : ""}`}>
        {value}
      </dd>
    </div>
  );
}

function formatMoney(cents: number | null, currency: string | null): string {
  if (cents == null) return "-";
  return `${(cents / 100).toFixed(2)} ${(currency ?? "usd").toUpperCase()}`;
}
